// visibility 축만 표현한다. 자동 검증 결과는 별도 `validation_status` 축이 소유한다.
// 그래서 status는 invalid 값을 갖지 않는다.
export type EpisodeStatus = "pending" | "available" | "orphaned" | "excluded";

/**
 * 에피소드에 붙은 자유 어휘 한 건. 키와 값의 쌍이다(`object: rope`).
 *
 * 이 축은 카탈로그(`Label`)에 넣을 수 없는 열린 값을 받는다. 그래서 어휘 계약에 포함되지 않는다.
 * 키도 자유 입력이다. 서버가 `normalizeTermKey` 와 같은 규칙으로 정규화한다. 사전 자체는
 * `/v1/label-terms` 가 소유한다.
 *
 * `canonical_*` 은 이 어휘가 뜻하는 온톨로지 항이다. **카탈로그에 이미 있는 쌍일 때만**
 * 연결된다. 없으면 오류 없이 null 로 남고 큐레이션 큐에 나타난다(422 가 아니다).
 *
 * `source` 는 어휘를 붙인 경로다. 저장 경로는 자기 경로(`manual`)의 행만 소유한다. 그래서 폼이
 * 다른 경로가 붙인 어휘를 payload 에서 빼도 그 행은 남는다. 지울 수 없는 어휘를 지울 수 있는
 * 것처럼 보여주지 않으려면 이 값을 확인해야 한다.
 */
export type EpisodeTerm = {
  term_key: string;
  term_value: string;
  canonical_key: string | null;
  canonical_value: string | null;
  source: string;
};

// 사람이 화면에서 붙인 어휘의 부여 경로. 이 값이 아닌 행은 폼이 지울 수 없다.
export const MANUAL_TERM_SOURCE = "manual";

// 서버 `LABEL_TERM_KEY_MAX_LENGTH`·`LABEL_TERM_VALUE_MAX_LENGTH` 와 같다. 길이는 code point 로 센다.
export const TERM_KEY_MAX_LENGTH = 32;
export const TERM_VALUE_MAX_LENGTH = 256;

export type TermKeyResult =
  | { ok: true; key: string }
  | { ok: false; reason: "blank" | "too_long" | "invalid_char" };

// 서버 키 규칙 `\w` 와 같은 문자 집합이다(유니코드 문자·숫자·밑줄).
const TERM_KEY_PATTERN = /^[\p{L}\p{N}_]+$/u;

/**
 * 키 입력을 사전 키로 바꾼다. 서버 `normalize_term_key` 와 같은 규칙이다.
 *
 * 앞뒤 공백 제거 → 공백 덩어리를 `_` 하나로 → 소문자. 그 뒤 빈 키, 32자 초과, 문자·숫자·`_`
 * 밖의 문자를 거부한다. 서버가 422 로 거부할 키를 저장 전에 알린다.
 *
 * @param raw 받은 키. 예) `"  Object  Name "`
 * @returns 정규화한 키 또는 거부 사유. 예) `{ ok: true, key: "object_name" }`
 */
export function normalizeTermKey(raw: string): TermKeyResult {
  const key = raw.trim().split(/\s+/u).join("_").toLowerCase();
  if (key === "") {
    return { ok: false, reason: "blank" };
  }
  if ([...key].length > TERM_KEY_MAX_LENGTH) {
    return { ok: false, reason: "too_long" };
  }
  if (!TERM_KEY_PATTERN.test(key)) {
    return { ok: false, reason: "invalid_char" };
  }
  return { ok: true, key };
}

/**
 * 값을 사전 정규화형으로 바꾼다. 서버 `normalize_term_value` 와 같은 규칙이다.
 *
 * @param value 원문 값. 예) `"  Gori   Fail "`
 * @returns 공백을 접고 소문자로 바꾼 값. 예) `"gori fail"`
 */
export function normalizeTermValue(value: string): string {
  return value.trim().split(/\s+/u).join(" ").toLowerCase();
}
